import { Box, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import QRCode from 'react-qr-code';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Skeleton } from '@/components';
import { urls } from '@/constants';
import { AnalyticsService, EVENTS } from '@/services';
import {
  selectSessionUserId,
  uiActions,
  useGenAppLoginMagicLinkQuery,
} from '@/store';
import { convertMsToHHMMSS } from '@/utils';

import { AppLoginModalLayout } from './AppLoginModalLayout';
import { useCountdown } from './useCountdown';

export const AppLoginModalContent = () => {
  const dispatch = useDispatch();
  const userId = useSelector(selectSessionUserId);
  const { data, refetch, isLoading, isFetching } = useGenAppLoginMagicLinkQuery(
    userId ? undefined : skipToken,
  );

  const { magicLinkToken, expiresAt } = data ?? {};
  const tokenParam = magicLinkToken ? `?magicLinkToken=${magicLinkToken}` : '';
  const qrCodeValue = urls.appLogin + tokenParam;
  const timeLeft = useCountdown(expiresAt);

  const onClose = () => {
    dispatch(uiActions.closeActiveModal());
  };

  const regenerateMagicLink = () => {
    AnalyticsService.logEvent(EVENTS.APP_LOGIN_MAGIC_LINK_EVENTS.REGENERATE);
    refetch();
  };

  const showSkeleton = isLoading || timeLeft === null || !magicLinkToken;

  return (
    <AppLoginModalLayout onModalClose={onClose}>
      <Box sx={styles.root}>
        <Typography variant="h2">
          Securely Log In to the LumiQ Mobile App
        </Typography>
        <Typography variant="body2">
          This is a one-time, encrypted code that is valid for a limited time
          and can only be used to access your account.
        </Typography>
        <Typography variant="body2">
          Scan this QR code with your mobile device to securely log in to the
          LumiQ app. If you don&apos;t have the app installed yet, scanning will
          take you directly to the App Store or Google Play to download it.
        </Typography>
        <Box sx={styles.qrCodeContainer}>
          {showSkeleton ? (
            <>
              <Skeleton variant="rectangular" width={160} height={160} />
              <Skeleton variant="text" width={160} height={20} />
              <Skeleton variant="rounded" width={160} height={32} />
            </>
          ) : (
            <>
              <QRCode
                style={{ opacity: timeLeft ? 1 : 0.25 }}
                value={qrCodeValue}
                size={160}
                aria-label="Login QR code"
              />
              <Typography
                variant="body2"
                sx={{
                  minWidth: 110,
                  textAlign: timeLeft ? 'left' : 'center',
                }}>
                {timeLeft
                  ? `Expires in ${convertMsToHHMMSS(timeLeft)}`
                  : 'Code expired'}
              </Typography>
              <Button
                sx={{ opacity: timeLeft ? 0 : 1 }}
                disabled={!!timeLeft}
                label="Generate new code"
                fullWidth={false}
                isLoading={isFetching}
                onClick={regenerateMagicLink}
              />
            </>
          )}
        </Box>
      </Box>
    </AppLoginModalLayout>
  );
};

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  qrCodeContainer: {
    mt: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: 2,
  },
};
